import { CheckPrimaryIcon, PencilGray400Icon, PlusWhiteIcon, TrashCanErrorIcon } from '@assets/iconComponents';
import { CollectionUpsertModal, Frame, GuideModal, MenuTop, SearchBar, SecureImage, Select, Text } from '@components';
import { CollectionSortTypeEnum, styleConstant } from '@constant';
import { useCollection } from '@hooks';
import { Button, MenuItem } from '@mui/material';
import { colorSet } from '@theme';
import { ICollection } from '@types';

const CollectionListPage = () => {
  const {
    data,
    flattenCollectionList,
    outputBottomRef,
    createQueryStringForCollectionList,
    inputKeyword,
    handleKeyDown,
    handleInputKeywordChange,
    handleClear,
    handleSubmit,
    sortType,
    setSortType,
    handleCollectionClick,
    isCollectionUpsertModalVisible,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,
    targetCollectionId,
    initialCollectionName,
    isCollectionDeleteModalVisible,
    handleCollectionDeleteModalOpen,
    handleCollectionDeleteModalClose,
    handleCollectionDeleteConfirmClick
  } = useCollection();

  // 정렬 기준 Select 컴포넌트의 value를 영어가 아닌 한국어로 보여주기 위한 변수
  const SORT_LABELS: Record<CollectionSortTypeEnum, string> = {
    [CollectionSortTypeEnum.RECENT_MODIFIED]: '최근 수정한 날짜',
    [CollectionSortTypeEnum.NAME]: '이름'
  };

  return (
    <>
      <MenuTop />

      <div
        style={{
          padding: '24px 40px',
          position: 'sticky',
          top: styleConstant.menuTopHeight,
          backgroundColor: 'white',
          zIndex: styleConstant.zIndexTopFilter,
          borderBottom: `1px solid ${colorSet.gray300}`
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}
        >
          <Text variant="h2" fontWeight="bold">
            컬렉션
          </Text>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleCollectionUpsertModalOpen();
            }}
          >
            <PlusWhiteIcon style={{ width: 20, height: 20, marginRight: 10 }} />
            <Text variant="body1" fontWeight="bold" color="white">
              새 컬렉션 추가
            </Text>
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <SearchBar
            placeholder="컬렉션 이름을 검색하세요."
            value={inputKeyword}
            options={[]}
            onKeyDown={handleKeyDown}
            onInputChange={handleInputKeywordChange}
            onClear={handleClear}
            onSubmit={handleSubmit}
            boxstyle={{ width: 560 }}
          />
          <Select
            value={sortType}
            renderValue={(value) => {
              const sortType = value as CollectionSortTypeEnum;
              return SORT_LABELS[sortType] || '정렬 기준';
            }}
            onChange={(e) => {
              setSortType(e.target.value);
            }}
            boxstyle={{ width: 160 }}
          >
            <MenuItem value={''} disabled>
              정렬 기준
            </MenuItem>
            <MenuItem value={CollectionSortTypeEnum.RECENT_MODIFIED}>최근 수정한 날짜</MenuItem>
            <MenuItem value={CollectionSortTypeEnum.NAME}>이름</MenuItem>
          </Select>
        </div>
      </div>

      {/* 컬렉션 리스트 */}
      <Frame width="100%" padding="48px 40px 96px 40px">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: `20px 10px`,
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          {data &&
            flattenCollectionList().length > 0 &&
            flattenCollectionList().map((item: ICollection) => {
              return (
                <div
                  key={item.id}
                  onClick={() => {
                    handleCollectionClick(item.isUsedImages, item.id);
                  }}
                  style={{
                    width: 'calc((100% - 40px) / 5)',
                    height: 380,
                    border: `1px solid ${colorSet.gray300}`,
                    padding: 20,
                    backgroundColor: item.isUsedImages ? colorSet.gray100 : colorSet.white,
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ width: '100%', height: '100%' }}>
                    {item.isUsedImages ? (
                      <div
                        style={{
                          height: 48,
                          marginBottom: '20px'
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <CheckPrimaryIcon style={{ width: 14, height: 14, marginRight: 8 }} />
                            <Text variant="subtitle" fontWeight="bold">
                              사용한 이미지
                            </Text>
                          </div>
                          <Text variant="body2">{item.imageCount ? item.imageCount.toLocaleString() : 0}장</Text>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          height: 48,
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '20px'
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Text variant="subtitle" fontWeight="bold">
                            {item.name}
                          </Text>
                          <Text variant="body2">{item.imageCount ? item.imageCount.toLocaleString() : 0}장</Text>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                          <PencilGray400Icon
                            style={{ width: 20, height: 20, cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation(); // 이벤트 전파 방지
                              handleCollectionUpsertModalOpen(item.id, item.name);
                            }}
                          />
                          <TrashCanErrorIcon
                            style={{ width: 20, height: 20, cursor: 'pointer' }}
                            onClick={(event) => {
                              event.stopPropagation(); // 이벤트 전파 방지
                              handleCollectionDeleteModalOpen(item.id);
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div style={{ width: '100%', height: 'calc(100% - 68px)' }}>
                      <div
                        style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', gap: '2px' }}
                      >
                        {/* 상단 영역 */}
                        <div
                          style={{
                            width: '100%',
                            height: '50%',
                            backgroundColor: colorSet.gray100,
                            position: 'relative'
                          }}
                        >
                          {item.isUsedImages ||
                          (item.thumbnails &&
                            item.thumbnails.length > 0 &&
                            item.thumbnails[0] &&
                            item.thumbnails[0].isUsed) ? (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)'
                              }}
                            />
                          ) : null}
                          {item.thumbnails && item.thumbnails.length > 0 && item.thumbnails[0] ? (
                            <SecureImage
                              imageUrl={item.thumbnails[0].s3Url}
                              imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : null}
                        </div>
                        {/* 하단 영역 */}
                        <div
                          style={{
                            display: 'flex',
                            width: '100%',
                            height: '50%',
                            gap: '2px'
                          }}
                        >
                          {/* 왼쪽 하단 */}
                          <div
                            style={{
                              flex: 1,
                              backgroundColor: colorSet.gray100,
                              position: 'relative'
                            }}
                          >
                            {item.isUsedImages ||
                            (item.thumbnails &&
                              item.thumbnails.length > 0 &&
                              item.thumbnails[1] &&
                              item.thumbnails[1].isUsed) ? (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                                }}
                              />
                            ) : null}
                            {item.thumbnails && item.thumbnails.length > 0 && item.thumbnails[1] ? (
                              <SecureImage
                                imageUrl={item.thumbnails[1].s3Url}
                                imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : null}
                          </div>
                          {/* 오른쪽 하단 */}
                          <div
                            style={{
                              flex: 1,
                              backgroundColor: colorSet.gray100,
                              position: 'relative'
                            }}
                          >
                            {item.isUsedImages ||
                            (item.thumbnails &&
                              item.thumbnails.length > 0 &&
                              item.thumbnails[2] &&
                              item.thumbnails[2].isUsed) ? (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                                }}
                              />
                            ) : null}
                            {item.thumbnails && item.thumbnails.length > 0 && item.thumbnails[2] ? (
                              <SecureImage
                                imageUrl={item.thumbnails[2].s3Url}
                                imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        <div ref={outputBottomRef} />

        {(!data || flattenCollectionList().length === 0) && (
          <div
            style={{
              height: '100px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Text variant="body1" color={colorSet.gray500}>
              검색된 컬렉션이 없습니다.
            </Text>
          </div>
        )}
      </Frame>

      <CollectionUpsertModal
        isVisible={isCollectionUpsertModalVisible}
        onClose={handleCollectionUpsertModalClose}
        targetCollectionId={targetCollectionId}
        initialCollectionName={initialCollectionName}
        createQueryStringForCollectionList={createQueryStringForCollectionList}
      />

      <GuideModal
        isVisible={isCollectionDeleteModalVisible}
        onClick={handleCollectionDeleteConfirmClick}
        onClose={handleCollectionDeleteModalClose}
        title={'컬렉션을 삭제하시겠습니까?'}
        content={'이 작업은 되돌릴 수 없습니다.'}
        buttonText="삭제하기"
        buttonVariant="contained"
        buttonCount={2}
      />
    </>
  );
};

export default CollectionListPage;
